import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Machine, MachineType, Customer, Integrator } from '../types';
import { machineApi, machineTypeApi, customerApi, photoApi } from '../api/machines';
import { integratorApi } from '../api/integrators';
import { useAuth } from '../context/AuthContext';
import { useIntegratorContext } from '../context/IntegratorContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { customerLabel } from '../utils/customerLabel';
import { Toast, useToast } from '../components/Toast';
import './Machines.css';

const PER_PAGE = 9;

/** Kafelek-licznik (liczba + etykieta) — spójny styl przy nagłówku i w paginacji. */
const CountTile: React.FC<{ value: React.ReactNode; label: string; title?: string }> = ({ value, label, title }) => (
  <div
    title={title}
    style={{
      background: 'var(--color-accent-tint)',
      border: '1px solid var(--color-accent-tint-strong)',
      borderRadius: 'var(--radius)',
      padding: '0.4rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      lineHeight: 1.1,
    }}
  >
    <span style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-accent)' }}>{value}</span>
    <span style={{
      fontSize: '0.62rem',
      color: 'var(--color-text-secondary)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginTop: '0.1rem',
    }}>{label}</span>
  </div>
);

const Machines: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isCustomer = user?.roles?.includes('CUSTOMER');
  const isAdmin = !!user?.roles?.includes('ADMIN');
  const [searchParams, setSearchParams] = useSearchParams();
  const [machines, setMachines] = useState<Machine[]>([]);
  // Łączna liczba maszyn (niezależna od filtrów typu/klienta) — do kafelka „Wszystkich maszyn".
  const [totalMachines, setTotalMachines] = useState<number | null>(null);
  const [types, setTypes] = useState<MachineType[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filterType, setFilterType] = useState<string>('');
  const [filterCustomerNip, setFilterCustomerNip] = useState<string>(() => searchParams.get('nip') || '');
  // Klient: filtr po integratorach, od których ma maszyny (opcje z pełnej listy maszyn).
  const [filterIntegrator, setFilterIntegrator] = useState<string>('');
  const [integratorOptions, setIntegratorOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [page, setPage] = useState(1);
  const { toast, showToast, dismissToast } = useToast();

  // Super-admin porusza się w globalnym kontekście integratora (picker w sidebarze).
  // Bez wybranego kontekstu nie pokazujemy tu żadnych maszyn.
  const { integrator: ctxIntegrator } = useIntegratorContext();

  useEffect(() => {
    const nipParam = searchParams.get('nip');
    if (nipParam && nipParam !== filterCustomerNip) {
      setFilterCustomerNip(nipParam);
      setFilterType('');
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFilters = useCallback(async () => {
    // Celowo bez Promise.all: awaria jednego źródła nie może zabić drugiego
    // (klientowi backend słusznie odmawia listy klientów → typy muszą wczytać się mimo to).
    try {
      const { data } = await machineTypeApi.getAll();
      setTypes(data);
    } catch (e) {
      console.error('Failed to load machine types', e);
    }
    // Lista klientów służy wyłącznie staffowi (filtr NIP + etykiety "Klient:").
    if (!isCustomer) {
      try {
        const { data } = await customerApi.getAll();
        setCustomers(data);
      } catch (e) {
        console.error('Failed to load customers', e);
      }
    }
  }, [isCustomer]);

  const loadMachines = useCallback(async () => {
    // Super-admin bez wybranego kontekstu integratora → pusta lista, zero requestów.
    // Z kontekstem backend (AccessGuard + nagłówek X-Integrator-Context) sam zawęża
    // wyniki wszystkich endpointów do maszyn tej firmy.
    if (isAdmin && !ctxIntegrator) {
      setMachines([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let res;
      if (filterType) {
        res = await machineApi.getByType(filterType);
      } else if (filterCustomerNip) {
        res = await machineApi.getByNip(filterCustomerNip);
      } else {
        res = await machineApi.getAll();
      }
      setMachines(res.data);
      // Opcje filtra integratorów budujemy z niefiltrowanej listy — zawsze komplet firm,
      // od których klient ma maszyny, niezależnie od aktualnie założonych filtrów.
      if (isCustomer && !filterType && !filterCustomerNip) {
        const byId = new Map<string, string>();
        (res.data as Machine[]).forEach((m) => {
          if (m.integratorId) byId.set(m.integratorId, m.integratorName || 'Integrator');
        });
        setIntegratorOptions(Array.from(byId, ([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (e) {
      console.error('Failed to load machines', e);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterCustomerNip, isAdmin, isCustomer, ctxIntegrator]);

  // Licznik „Wszystkich maszyn" — zawsze pełna lista integratora, niezależnie od filtrów
  // typu/klienta. Odświeżany przy zmianie kontekstu integratora i po dodaniu maszyny.
  const loadTotal = useCallback(async () => {
    if (isCustomer) return; // kafelek pokazujemy tylko staffowi (obok „Dodaj typ")
    if (isAdmin && !ctxIntegrator) { setTotalMachines(0); return; }
    try {
      const { data } = await machineApi.getAll();
      setTotalMachines(data.length);
    } catch (e) {
      console.error('Failed to load machine count', e);
    }
  }, [isCustomer, isAdmin, ctxIntegrator]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  useEffect(() => {
    loadTotal();
  }, [loadTotal]);

  const getTypeName = (typeId: string) =>
    types.find((t) => t.id === typeId)?.name || '—';

  const getCustomer = (nip: string) => customers.find((c) => c.nip === nip);
  // Filtr integratora działa po stronie klienta — lista maszyn klienta jest niewielka.
  const displayMachines = isCustomer && filterIntegrator
    ? machines.filter((m) => m.integratorId === filterIntegrator)
    : machines;

  // Paginacja: 9 maszyn na stronę. Strona przycinana do zakresu (po zmianie filtrów/listy).
  const totalPages = Math.max(1, Math.ceil(displayMachines.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = displayMachines.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [filterType, filterCustomerNip, filterIntegrator]);

  return (
    <div className="machines-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Maszyny</h1>
          {!isCustomer && !isMobile && (
            <CountTile
              value={totalMachines ?? '—'}
              label="Ilość"
              title="Łączna liczba maszyn — niezależnie od klienta"
            />
          )}
        </div>
        {!isCustomer && !isMobile && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/machine-types" className="btn btn-primary">
              Dodaj typ
            </Link>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              Dodaj maszynę
            </button>
          </div>
        )}
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label className="form-label">Typ maszyny</label>
          <select
            className="form-select"
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setFilterCustomerNip(''); }}
          >
            <option value="">Wszystkie typy</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {!isCustomer && (
          <div className="filter-group">
            <label className="form-label">Klient (NIP)</label>
            <select
              className="form-select"
              value={filterCustomerNip}
              onChange={(e) => { setFilterCustomerNip(e.target.value); setFilterType(''); }}
            >
              <option value="">Wszyscy klienci</option>
              {customers.map((c) => (
                <option key={c.id} value={c.nip}>{customerLabel(c)}</option>
              ))}
            </select>
          </div>
        )}

        {isCustomer && (
          <div className="filter-group">
            <label className="form-label">Integrator</label>
            <select
              className="form-select"
              value={filterIntegrator}
              onChange={(e) => setFilterIntegrator(e.target.value)}
            >
              <option value="">Wszyscy integratorzy</option>
              {integratorOptions.map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="empty-state">Ładowanie...</div>
      ) : isAdmin && !ctxIntegrator ? (
        <div className="empty-state">
          <p>Wybierz integratora w sidebarze („Kontekst integratora"), aby zobaczyć jego maszyny.</p>
          <p className="item-meta">Jako super-admin poruszasz się po Operacjach i Zgłoszeniach w kontekście jednej firmy.</p>
        </div>
      ) : displayMachines.length === 0 ? (
        <div className="empty-state">
          <p>Brak maszyn do wyświetlenia</p>
        </div>
      ) : (
        <>
        <div className="machines-grid">
          {pageItems.map((m) => {
            return (
              <Link to={`/machines/${m.id}`} key={m.id} className="machine-card card">
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <div style={{
                    width: 64, height: 64, flexShrink: 0,
                    borderRadius: 3, overflow: 'hidden',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {m.thumbnailUrl ? (
                      <img src={m.thumbnailUrl} alt={`${m.name} miniaturka`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>brak zdj.</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="machine-card-header">
                      <h3 className="machine-card-name">{m.name}</h3>
                      <span className="badge badge-primary">{getTypeName(m.machineTypeId)}</span>
                    </div>
                    <p className="machine-card-desc">{m.description || 'Brak opisu'}</p>
                  </div>
                </div>
                {/* Stopka poza kolumną treści — pełna szerokość kafelka, więc lewa krawędź
                    loga klienta jest na równi z lewą krawędzią miniatury maszyny. */}
                {/* Stopka: układ dwukolumnowy — logo klienta po lewej, po prawej nazwa
                    (wytłuszczona) nad NIP-em. Lewa krawędź loga na równi z miniaturą maszyny. */}
                <div className="machine-card-footer machine-card-customer">
                  {!isCustomer && (
                    getCustomer(m.ownerNip)?.logoUrl ? (
                      <img
                        src={getCustomer(m.ownerNip)!.logoUrl!}
                        alt=""
                        className="machine-card-customer-logo"
                      />
                    ) : (
                      <span className="machine-card-customer-logo machine-card-customer-logo--empty">
                        {(getCustomer(m.ownerNip)?.companyName || m.ownerNip || '?')[0]?.toUpperCase()}
                      </span>
                    )
                  )}
                  <div className="machine-card-customer-text">
                    {!isCustomer && (() => {
                      const c = getCustomer(m.ownerNip);
                      return (
                        <>
                          <span className="machine-card-customer-name">
                            {c?.companyName || `NIP ${m.ownerNip}`}
                          </span>
                          {c?.companyName && (
                            <span className="machine-card-customer-nip">NIP {m.ownerNip}</span>
                          )}
                        </>
                      );
                    })()}
                    {isCustomer && (
                      <span className="machine-card-customer-name">
                        {m.integratorName || '—'}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="machines-pagination">
          <button
            className="btn btn-secondary"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Poprzednia
          </button>
          <CountTile
            value={displayMachines.length}
            label={filterCustomerNip ? 'maszyn klienta' : 'wszystkich maszyn'}
            title="Liczba maszyn w bieżącym widoku"
          />
          <button
            className="btn btn-secondary"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Następna
          </button>
        </div>
        </>
      )}

      {showAddModal && (
        <AddMachineModal
          types={types}
          customers={customers}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); loadMachines(); loadTotal(); showToast('success', 'Maszyna dodana.'); }}
        />
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

interface AddMachineModalProps {
  types: MachineType[];
  customers: Customer[];
  onClose: () => void;
  onSaved: () => void;
}

const AddMachineModal: React.FC<AddMachineModalProps> = ({ types, customers, onClose, onSaved }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: '', description: '', ownerNip: '', machineTypeId: '' });
  const [ticketSystemEnabled, setTicketSystemEnabled] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Super-admin creates machines on behalf of an integrator; staff auto-use their own.
  const isAdmin = !!user?.roles?.includes('ADMIN');
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [integratorId, setIntegratorId] = useState('');

  useEffect(() => {
    if (isAdmin) {
      integratorApi.list().then(({ data }) => setIntegrators(data)).catch(() => {});
    }
  }, [isAdmin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setError('');
    if (!file) {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Zdjęcie musi być obrazem');
      e.target.value = '';
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data: created } = await machineApi.create({
        name: form.name,
        description: form.description,
        ownerNip: form.ownerNip,
        machineTypeId: form.machineTypeId,
        ticketSystemEnabled,
        ...(isAdmin && integratorId ? { integratorId } : {}),
      });
      if (thumbnailFile && created?.id && user?.userId) {
        try {
          const { data: photo } = await photoApi.upload(created.id, thumbnailFile, '', user.userId);
          await machineApi.setThumbnail(created.id, photo.id);
        } catch (thumbErr) {
          console.warn('Thumbnail upload failed', thumbErr);
        }
      }
      onSaved();
    } catch {
      setError('Nie udało się dodać maszyny');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Nowa maszyna</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label className="form-label">Nazwa</label>
            <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label className="form-label">Opis</label>
            <textarea className="form-input" name="description" value={form.description} onChange={handleChange} rows={3} maxLength={120} />
            <div className="item-meta" style={{ textAlign: 'right', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              {form.description.length}/120
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Typ maszyny</label>
            <select className="form-select" name="machineTypeId" value={form.machineTypeId} onChange={handleChange} required>
              <option value="">Wybierz typ</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className="form-group">
              <label className="form-label">Integrator (właściciel maszyny)</label>
              <select className="form-select" value={integratorId} onChange={(e) => setIntegratorId(e.target.value)} required>
                <option value="">Wybierz integratora</option>
                {integrators.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Klient (NIP)</label>
            <select className="form-select" name="ownerNip" value={form.ownerNip} onChange={handleChange} required>
              <option value="">Wybierz klienta</option>
              {customers.map((c) => (
                <option key={c.id} value={c.nip}>{customerLabel(c)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ticketSystemEnabled}
                onChange={(e) => setTicketSystemEnabled(e.target.checked)}
                style={{ marginTop: '0.2rem', flexShrink: 0 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>System zgłoszeń dla klientów</span>
              </span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Miniaturka (opcjonalnie)</label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{
                width: 80, height: 80, flexShrink: 0,
                borderRadius: 3, overflow: 'hidden',
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface-alt)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {thumbnailPreview ? (
                  <img src={thumbnailPreview} alt="Podgląd"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>brak</span>
                )}
              </div>
              <input type="file" accept="image/*" className="form-input" style={{ flex: 1, minWidth: 200 }}
                onChange={onThumbnailChange} />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Anuluj</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Zapisuję...' : 'Dodaj maszynę'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Machines;
